import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { downloadBlob } from "@/lib/api";
import {
  ArrowLeft, CheckCircle, AlertTriangle, XCircle, RefreshCw,
  FileText, Upload, Trash2, RotateCw, Download, Pencil,
} from "lucide-react";

const CATEGORY_LABELS = {
  permiso_sanitario: "Permiso sanitario",
  certificacion: "Certificacion",
  contrato_alquiler: "Contrato alquiler",
  contrato_empleado: "Contrato empleado",
  contrato_proveedor: "Contrato proveedor",
  producto_camara: "Producto en camara",
  marca: "Marca",  // Fix WH63 (2026-09-28)
  accesos_contables: "Accesos contables",  // Fix WH78 (2026-10-15)
  custom: "Personalizado",
};

const STATUS_CONFIG = {
  vigente: { label: "Vigente", color: "bg-green-100 text-green-800", Icon: CheckCircle },
  por_vencer: { label: "Por vencer", color: "bg-yellow-100 text-yellow-800", Icon: AlertTriangle },
  vencido: { label: "Vencido", color: "bg-red-100 text-red-800", Icon: XCircle },
  renovado: { label: "Renovado", color: "bg-blue-100 text-blue-800", Icon: CheckCircle },
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
}

export default function OpsVigenciaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vig, setVig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRenew, setShowRenew] = useState(false);
  const [renewDate, setRenewDate] = useState("");
  const [renewNotes, setRenewNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [editingAlerts, setEditingAlerts] = useState(false);
  const [alertForm, setAlertForm] = useState({ responsible_user_id: "", notify_user_ids: [], alert_anticipation_days: "" });
  const [savingAlerts, setSavingAlerts] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const res = await api.get(`/ops/vigencias`);
      const found = (res.data || []).find(v => v.id === id);
      setVig(found || null);
      if (found) {
        setAlertForm({
          responsible_user_id: found.responsible_user_id || "",
          notify_user_ids: found.notify_user_ids || [],
          alert_anticipation_days: (found.alert_anticipation_days || []).join(", "),
        });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { api.get("/ops/users/lite").then(r => setUsers(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/branches").then(r => setBranches(r.data || [])).catch(() => {}); }, []);

  const toggleNotifyUser = (userId) => {
    setAlertForm(f => ({
      ...f,
      notify_user_ids: f.notify_user_ids.includes(userId)
        ? f.notify_user_ids.filter(uid => uid !== userId)
        : [...f.notify_user_ids, userId],
    }));
  };

  const handleSaveAlerts = async () => {
    setSavingAlerts(true);
    try {
      const anticipation = alertForm.alert_anticipation_days
        .split(",").map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n >= 0);
      await api.put(`/ops/vigencias/${id}`, {
        responsible_user_id: alertForm.responsible_user_id || null,
        notify_user_ids: alertForm.notify_user_ids,
        alert_anticipation_days: anticipation.length > 0 ? anticipation : null,
      });
      setEditingAlerts(false);
      await fetch();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error al guardar.");
    } finally { setSavingAlerts(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/ops/vigencias/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await fetch();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error al subir el documento.");
    } finally { setUploading(false); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!confirm("Eliminar este documento?")) return;
    try {
      await api.delete(`/ops/vigencias/${id}/documents/${docId}`);
      await fetch();
    } catch (e) {
      alert(e?.response?.data?.detail || "No tienes permiso para borrar documentos.");
    }
  };

  // Fix WH11 (2026-08-30): los documentos se subían pero no había forma de
  // abrirlos (video de Claudia 25/08). Baja el archivo autenticado y lo abre
  // en otra pestaña; si el navegador bloquea el popup, lo descarga.
  const handleOpenDoc = async (docItem) => {
    try {
      const res = await api.get(`/ops/vigencias/${id}/documents/${docItem.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const win = window.open(url, "_blank");
      if (!win) downloadBlob(res.data, docItem.name || "documento");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e?.response?.data?.detail || "No se pudo abrir el documento.");
    }
  };

  const handleRenew = async () => {
    if (!renewDate) return;
    setRenewing(true);
    try {
      await api.post(`/ops/vigencias/${id}/renew`, { new_due_date: renewDate, notes: renewNotes });
      setShowRenew(false);
      await fetch();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error al renovar.");
    } finally { setRenewing(false); }
  };

  if (loading) return <div className="p-6 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!vig) return <div className="p-6 text-slate-500">Vigencia no encontrada. <button className="text-yellow-600 underline" onClick={() => navigate("/ops/vigencias")}>Volver</button></div>;

  const { status } = vig;
  const cfg = STATUS_CONFIG[status] || { label: status, color: "bg-slate-100 text-slate-600", Icon: AlertTriangle };
  const Icon = cfg.Icon;
  const days = daysUntil(vig.due_date);

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="vigencia-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/ops/vigencias")} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{vig.name}</h1>
            <p className="text-sm text-slate-500">{CATEGORY_LABELS[vig.category] || vig.category}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${cfg.color}`}>
            <Icon className="w-3.5 h-3.5" /> {cfg.label}
          </span>
          {/* Fix WH63 (2026-09-28): editar todos los datos de la vigencia. */}
          <button onClick={() => navigate(`/ops/vigencias/${id}/editar`)}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg font-semibold text-sm hover:bg-slate-50 transition-colors"
            data-testid="btn-editar-vigencia">
            <Pencil className="w-4 h-4" /> Editar
          </button>
          <button onClick={() => setShowRenew(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors"
            data-testid="btn-renovar">
            <RotateCw className="w-4 h-4" /> Renovar
          </button>
        </div>
      </div>

      {/* Info card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 grid grid-cols-2 gap-5">
        <div>
          <div className="text-xs text-slate-500 mb-0.5">Sucursal</div>
          <div className="font-medium text-slate-900">{branches.find(b => b.id === vig.branch_id)?.name || vig.branch_id || "—"}</div>
        </div>
        {/* Fix WH63 (2026-09-28): la empresa (en una marca, la titular). */}
        <div>
          <div className="text-xs text-slate-500 mb-0.5">{vig.category === "marca" ? "Empresa titular" : "Empresa"}</div>
          <div className="font-medium text-slate-900">{vig.legal_entity_name || "—"}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-0.5">Emisor</div>
          <div className="font-medium text-slate-900">{vig.issuer || "—"}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-0.5">{vig.category === "marca" ? "Fecha de concesion" : "Fecha emision"}</div>
          <div className="font-medium text-slate-900">{vig.issue_date || "—"}</div>
        </div>
        {vig.use_declaration_date && (
          <div>
            <div className="text-xs text-slate-500 mb-0.5">Declaracion de uso</div>
            <div className="font-medium text-slate-900" data-testid="vigencia-uso">{vig.use_declaration_date}</div>
          </div>
        )}
        <div>
          <div className="text-xs text-slate-500 mb-0.5">Fecha vencimiento</div>
          <div className={`font-bold ${days !== null && days < 0 ? "text-red-600" : days !== null && days <= 7 ? "text-yellow-600" : "text-slate-900"}`}>
            {vig.due_date}
            {days !== null && (
              <span className="ml-2 text-sm font-normal">
                ({days < 0 ? `vencio hace ${Math.abs(days)} dias` : days === 0 ? "vence hoy" : `${days} dias`})
              </span>
            )}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-0.5">Frecuencia renovacion</div>
          <div className="font-medium text-slate-900 capitalize">{vig.renewal_frequency}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-0.5">Alertas configuradas</div>
          <div className="font-medium text-slate-900">{(vig.alert_anticipation_days || []).join(", ")} dias antes</div>
        </div>
        {vig.description && (
          <div className="col-span-2">
            <div className="text-xs text-slate-500 mb-0.5">Descripcion</div>
            <div className="text-sm text-slate-700">{vig.description}</div>
          </div>
        )}
      </div>

      {/* Alertas y responsables */}
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="vigencia-alerts-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900">Alertas y responsables</h3>
          {!editingAlerts && (
            <button onClick={() => setEditingAlerts(true)} className="text-xs font-semibold text-yellow-700 hover:underline" data-testid="btn-editar-alertas">
              Editar
            </button>
          )}
        </div>
        {!editingAlerts ? (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs text-slate-500 mb-0.5">Responsable</div>
              <div className="font-medium text-slate-900">
                {users.find(u => u.id === vig.responsible_user_id)?.full_name || (vig.responsible_user_id ? vig.responsible_user_id : "Sin asignar")}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-0.5">Destinatarios adicionales</div>
              <div className="font-medium text-slate-900">
                {(vig.notify_user_ids || []).length === 0 ? "Ninguno" :
                  vig.notify_user_ids.map(uid => users.find(u => u.id === uid)?.full_name || uid).join(", ")}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Responsable</label>
              <select value={alertForm.responsible_user_id} onChange={e => setAlertForm(f => ({ ...f, responsible_user_id: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="vigencia-edit-responsable-select">
                <option value="">Sin responsable asignado</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email} ({u.role})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Destinatarios adicionales</label>
              <div className="border border-slate-200 rounded-lg p-2 max-h-40 overflow-y-auto space-y-1">
                {users.map(u => (
                  <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer text-sm">
                    <input type="checkbox" checked={alertForm.notify_user_ids.includes(u.id)}
                      onChange={() => toggleNotifyUser(u.id)} className="w-4 h-4" />
                    <span>{u.full_name || u.email} <span className="text-slate-400">({u.role})</span></span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Días de anticipación</label>
              <input type="text" value={alertForm.alert_anticipation_days}
                onChange={e => setAlertForm(f => ({ ...f, alert_anticipation_days: e.target.value }))}
                placeholder="60, 30, 7"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="vigencia-edit-anticipation-input" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setEditingAlerts(false)} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={handleSaveAlerts} disabled={savingAlerts}
                className="flex-1 py-2 bg-[#FFDF00] text-slate-900 rounded-lg text-sm font-semibold hover:bg-yellow-400 disabled:opacity-50"
                data-testid="btn-guardar-alertas">
                {savingAlerts ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Documents */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900">Documentos ({vig.documents?.length || 0})</h3>
          <label className={`flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium cursor-pointer transition-colors ${uploading ? "opacity-50" : ""}`}>
            <Upload className="w-3.5 h-3.5" /> {uploading ? "Subiendo..." : "Subir"}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
        </div>
        {vig.documents?.length === 0 ? (
          <p className="text-sm text-slate-400">Sin documentos. Sube el PDF del permiso o certificado para tenerlo a mano.</p>
        ) : (
          <div className="space-y-2">
            {vig.documents.map(doc => (
              <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 cursor-pointer flex-1 min-w-0" onClick={() => handleOpenDoc(doc)} data-testid={`doc-abrir-${doc.id}`}>
                  <FileText className="w-4 h-4 text-slate-400" />
                  <div>
                    <div className="text-sm font-medium text-slate-900 hover:underline">{doc.name}</div>
                    <div className="text-xs text-slate-500">{new Date(doc.uploaded_at).toLocaleDateString("es-MX")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleOpenDoc(doc)} data-testid={`btn-abrir-documento-${doc.id}`} title="Abrir / descargar" className="p-1.5 text-slate-400 hover:text-slate-900 transition-colors">
                    <Download className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDeleteDoc(doc.id)} data-testid={`btn-borrar-documento-${doc.id}`} className="p-1.5 text-slate-400 hover:text-red-600 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Renewal history */}
      {vig.renewal_history?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Historial de renovaciones</h3>
          <div className="space-y-3">
            {vig.renewal_history.map((h, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <RotateCw className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium">{h.previous_due_date} → {h.new_due_date}</div>
                  <div className="text-xs text-slate-500">{new Date(h.renewed_at).toLocaleDateString("es-MX")} {h.notes && `— ${h.notes}`}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Renew modal */}
      {showRenew && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowRenew(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-slate-900">Renovar vigencia</h3>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nueva fecha de vencimiento</label>
              <input type="date" value={renewDate} onChange={e => setRenewDate(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notas (opcional)</label>
              <input type="text" value={renewNotes} onChange={e => setRenewNotes(e.target.value)}
                placeholder="N° de tramite, observaciones..."
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowRenew(false)} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={handleRenew} disabled={!renewDate || renewing}
                className="flex-1 py-2 bg-[#FFDF00] text-slate-900 rounded-lg text-sm font-semibold hover:bg-yellow-400 disabled:opacity-50"
                data-testid="btn-confirmar-renovacion">
                {renewing ? "Guardando..." : "Renovar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
